import { jsPDF } from 'jspdf'

/**
 * Invoice PDF, drawn with jsPDF's vector primitives.
 *
 * The old version screenshotted the DOM with html2canvas and pasted the bitmap
 * into the page, which is why the output was blurry (a ~72 DPI raster, then JPEG
 * compressed) and looked odd — the "cells" were disabled MUI text inputs, so the
 * invoice came out full of grey rounded boxes. Drawing it directly keeps the text
 * as real text: sharp at any zoom, selectable/searchable, and a fraction of the
 * file size. It also drops a hidden off-screen DOM copy of the whole invoice.
 *
 * Note: the built-in PDF fonts have no rupee glyph, so amounts are plain numbers
 * and the columns are labelled "(INR)".
 */

const A4 = { w: 595.28, h: 841.89 }
const M = 40                       // page margin
const BRAND = [173, 73, 112]       // #ad4970
const INK = [31, 41, 55]
const MUTED = [17, 24, 39]
const LABEL = [107, 114, 128]
const RULE = [229, 231, 235]
const ZEBRA = [247, 249, 250]

const money = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '-'
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const text = (v) => {
  if (v === null || v === undefined || v === '') return '-'
  if (Array.isArray(v)) return v.length ? v.join(', ') : '-'
  return String(v)
}

// Date-only values arrive as UTC midnight; read the calendar date off the string
// rather than parsing into local time (which can roll back a day).
const day = (v) => {
  if (!v) return '-'
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v)
}

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })

// The API returns only price/tax/quantity — `amount` and `tax_amount` are derived
// by whichever screen is rendering the grid. Recompute them here when they're
// absent so the invoice is correct no matter which screen opened it.
const withDerivedAmounts = (rows = []) =>
  rows.map((r) => {
    const price = Number(r.selling_price) || 0
    const qty = Number(r.quantity) || 0
    const taxPct = Number(r.tax) || 0
    const taxAmount = r.tax_amount != null ? Number(r.tax_amount) : (price * taxPct) / 100 * qty
    const amount = r.amount != null ? Number(r.amount) : (price + (price * taxPct) / 100) * qty
    return { ...r, tax_amount: taxAmount, amount }
  })

export async function generateInvoicePdf({ rowData = {}, spareData = [], labourData = [], logoSrc, businessName }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true })
  let y = M

  // ---------- header ----------
  const logo = logoSrc ? await loadImage(logoSrc) : null
  if (logo) {
    const h = 34
    const w = (logo.width / logo.height) * h
    try { doc.addImage(logo, 'PNG', M, y, Math.min(w, 130), h) } catch { /* logo is optional */ }
  }

  doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(...BRAND)
  doc.text('INVOICE', A4.w - M, y + 18, { align: 'right' })
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...LABEL)
  const sub = [businessName, rowData.invoice_number ? `Invoice #${rowData.invoice_number}` : null]
    .filter(Boolean).join('  ·  ')
  if (sub) doc.text(sub, A4.w - M, y + 32, { align: 'right' })

  y += 52
  doc.setDrawColor(...BRAND).setLineWidth(1.2).line(M, y, A4.w - M, y)
  y += 20

  // ---------- detail blocks (two columns) ----------
  const colW = (A4.w - M * 2 - 24) / 2
  const block = (title, pairs, x, top) => {
    doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(...MUTED)
    doc.text(title, x, top)
    doc.setDrawColor(...BRAND).setLineWidth(1)
    doc.line(x, top + 3, x + 26, top + 3)

    let ly = top + 16
    doc.setFontSize(8.5)
    pairs.forEach(([k, v]) => {
      doc.setFont('helvetica', 'normal').setTextColor(...LABEL)
      doc.text(String(k), x, ly)
      doc.setTextColor(...INK)
      // wrap long values (addresses, emails) inside the column
      const lines = doc.splitTextToSize(text(v), colW - 96)
      doc.text(lines, x + 92, ly)
      ly += 12 * lines.length
    })
    return ly
  }

  const rowOfBlocks = (left, right) => {
    const a = block(left[0], left[1], M, y)
    const b = block(right[0], right[1], M + colW + 24, y)
    y = Math.max(a, b) + 14
  }

  rowOfBlocks(
    ['Vehicle Details', [
      ['Vehicle Number', rowData.vehicle_number],
      ['Model', rowData.model],
      ['Manufacturer', rowData.brand],
      ['Fuel Type', rowData.fuel_type],
      ['KM Driven', rowData.kilometers_driven],
    ]],
    ['Customer Details', [
      ['Name', rowData.name],
      ['Pickup Address', rowData.pickup_address],
      ['Mobile', rowData.mobile_number],
      ['Email', rowData.email],
    ]],
  )

  rowOfBlocks(
    ['Appointment Details', [
      ['Status', rowData.appointment_status],
      ['Date', day(rowData.appointment_date)],
      ['Time', rowData.appointment_time],
    ]],
    ['Assigned Team', [
      ['Advisor', rowData.advisor_name],
      ['Technicians', rowData.technician_name],
    ]],
  )

  rowOfBlocks(
    ['Estimate / Job Card', [
      ['Estimate No.', rowData.estimate_number],
      ['Estimate Date', day(rowData.estimate_created_on)],
      ['Job Card No.', rowData.jobcard_number],
      ['Opened On', day(rowData.jobcard_opened_on)],
    ]],
    ['Invoice Details', [
      ['Created By', rowData.invoice_created_by],
      ['Job Completed', day(rowData.service_completed_on)],
      ['Payment Status', rowData.payment_status],
      ['Payment Method', rowData.payment_method],
    ]],
  )

  // ---------- line-item tables ----------
  const COLS = [
    { key: 'name', title: 'Description', w: 150, align: 'left' },
    { key: 'quantity', title: 'Qty', w: 40, align: 'right' },
    { key: 'hsn_sac', title: 'HSN/SAC', w: 75, align: 'left' },
    { key: 'selling_price', title: 'Price', w: 65, align: 'right', money: true },
    { key: 'tax', title: 'Tax %', w: 45, align: 'right' },
    { key: 'tax_amount', title: 'Tax Amt', w: 60, align: 'right', money: true },
    { key: 'amount', title: 'Amount', w: 80, align: 'right', money: true },
  ]
  const TABLE_W = COLS.reduce((s, c) => s + c.w, 0)
  const ROW_H = 18

  const newPageIfNeeded = (needed) => {
    if (y + needed <= A4.h - M - 24) return
    doc.addPage()
    y = M
  }

  const drawTable = (title, rows) => {
    if (!rows?.length) return 0
    newPageIfNeeded(46 + ROW_H)

    doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(...MUTED)
    doc.text(title, M, y)
    doc.setDrawColor(...BRAND).setLineWidth(1)
    doc.line(M, y + 3, M + 26, y + 3)
    y += 14

    // header row
    doc.setFillColor(...ZEBRA).rect(M, y, TABLE_W, ROW_H, 'F')
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...MUTED)
    let x = M
    COLS.forEach((c) => {
      const tx = c.align === 'right' ? x + c.w - 6 : x + 6
      doc.text(c.title, tx, y + 12, { align: c.align })
      x += c.w
    })
    y += ROW_H

    // body
    doc.setFont('helvetica', 'normal').setFontSize(8.5)
    let subtotal = 0
    rows.forEach((r, i) => {
      newPageIfNeeded(ROW_H)
      if (i % 2) doc.setFillColor(252, 253, 253).rect(M, y, TABLE_W, ROW_H, 'F')

      x = M
      COLS.forEach((c) => {
        const raw = r[c.key]
        const val = c.money ? money(raw) : text(raw)
        doc.setTextColor(...INK)
        const tx = c.align === 'right' ? x + c.w - 6 : x + 6
        const clipped = doc.splitTextToSize(val, c.w - 12)[0] || '-'
        doc.text(clipped, tx, y + 12, { align: c.align })
        x += c.w
      })

      doc.setDrawColor(...RULE).setLineWidth(0.5).line(M, y + ROW_H, M + TABLE_W, y + ROW_H)
      subtotal += Number(r.amount) || 0
      y += ROW_H
    })

    // subtotal
    doc.setFont('helvetica', 'bold').setFontSize(8.5).setTextColor(...MUTED)
    doc.text(`${title} total`, M + TABLE_W - 140, y + 13, { align: 'right' })
    doc.text(money(subtotal), M + TABLE_W - 6, y + 13, { align: 'right' })
    y += ROW_H + 12
    return subtotal
  }

  const spareTotal = drawTable('Spares', withDerivedAmounts(spareData))
  const labourTotal = drawTable('Labour', withDerivedAmounts(labourData))

  // ---------- grand total ----------
  const grand = (spareTotal || 0) + (labourTotal || 0)
  newPageIfNeeded(46)
  const boxW = 240
  const boxX = A4.w - M - boxW
  doc.setFillColor(...BRAND).rect(boxX, y, boxW, 30, 'F')
  doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(255, 255, 255)
  doc.text('Grand Total (INR)', boxX + 12, y + 19)
  doc.setFontSize(12)
  doc.text(money(rowData.invoice_amount ?? grand), boxX + boxW - 12, y + 19, { align: 'right' })
  y += 30

  // ---------- footer on every page ----------
  const pages = doc.getNumberOfPages()
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p)
    doc.setDrawColor(...RULE).setLineWidth(0.5).line(M, A4.h - M + 6, A4.w - M, A4.h - M + 6)
    doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(...LABEL)
    doc.text('This is a computer-generated invoice.', M, A4.h - M + 18)
    doc.text(`Page ${p} of ${pages}`, A4.w - M, A4.h - M + 18, { align: 'right' })
  }

  const who = text(rowData.name).replace(/[^\w\s-]/g, '').trim() || 'Customer'
  const num = rowData.invoice_number ? `-${rowData.invoice_number}` : ''
  doc.save(`Invoice${num} - ${who}.pdf`)
}

export default generateInvoicePdf
